import { getBobLevel } from './67-bob-levels.mjs';

// Fixed authored mechanics, not random difficulty. Units: gravity m/s²,
// response s⁻¹. Every world preserves the same steering and Bounce controls.
const profiles = [
  ['Tailwind lanes', 'Follow gold lanes for a gentle forward push.', 11.5, 12, 'boost', '#ffe17b'],
  ['Ice drift', 'Steer early: ice preserves sideways momentum.', 11.5, 3, 'ice', '#b5edff'],
  [
    'Moss grip',
    'Moss lanes brake sideways drift for precise landings.',
    11.5,
    18,
    'grip',
    '#79df88',
  ],
  ['Jelly lift', 'Pink jelly lanes give low bounces extra lift.', 9, 12, 'lift', '#ff90c9'],
  [
    'Desert thermals',
    'Gold columns lift you while you stay inside them.',
    11.5,
    12,
    'thermal',
    '#ffc96b',
  ],
  [
    'Tidal currents',
    'The tide sways sideways. Countersteer to hold your route.',
    10.5,
    10,
    'tide',
    '#5fdad8',
  ],
  [
    'Volcanic vents',
    'Orange vents pulse upward. Watch their bright phase.',
    12,
    12,
    'vent',
    '#ff905b',
  ],
  [
    'Cloud glide',
    'Long cloud arcs need earlier steering. Blue lanes slow your fall.',
    6,
    8,
    'glide',
    '#d0edff',
  ],
  [
    'Bubble currents',
    'Water slows forward travel. Bubble columns lift you gently.',
    6.5,
    9,
    'bubble',
    '#66e3ef',
  ],
  [
    'Orbital bounce',
    'Low gravity makes long arcs; release steering early to settle.',
    6,
    5,
    'orbit',
    '#b0a0ff',
  ],
  [
    'Neon express',
    'Cyan lanes accelerate you. Line up the next gate before entering.',
    11.5,
    14,
    'express',
    '#61ffdc',
  ],
  [
    'Petal breeze',
    'Pink lanes carry a slow side breeze. Steer against the petals.',
    10.5,
    12,
    'petal',
    '#ffb5da',
  ],
  [
    'Crystal weight',
    'Heavy gravity shortens arcs. Prisms restore lift near the floor.',
    15,
    14,
    'prism',
    '#c7b4ff',
  ],
  ['Leaf drafts', 'Amber lanes alternate left and right drafts.', 11.5, 10, 'leaf', '#eeb46c'],
  [
    'Toy conveyors',
    'Striped belts move you forward and sideways together.',
    11.5,
    12,
    'belt',
    '#f2b8ff',
  ],
  [
    'Vine channels',
    'Green channels gently pull you toward their centre.',
    11.5,
    16,
    'vine',
    '#87cf77',
  ],
  [
    'Moon blooms',
    'Lunar gravity stretches jumps. Bloom lanes lift descending bounces.',
    7,
    7,
    'bloom',
    '#cbd0ff',
  ],
  [
    'Rainbow surf',
    'Ride low over the moving water for speed, then Bounce off a crest.',
    9,
    10,
    'surf',
    '#78e7ea',
  ],
  [
    'Aurora slide',
    'Slippery steering and a gentle crosswind reward early corrections.',
    10,
    4,
    'aurora',
    '#8fffe2',
  ],
  [
    'Celestial tides',
    'Marked violet sectors lower gravity; golden gaps restore weight.',
    11.5,
    10,
    'celestial',
    '#dfb4ff',
  ],
].map(([name, hint, gravity, response, kind, colour]) =>
  Object.freeze({ name, hint, gravity, response, kind, colour }),
);

/** Immutable world rules for a validated authored level (1..20). */
export function getBobWorld(level) {
  if (!Number.isInteger(level) || !profiles[level - 1]) throw new RangeError('Invalid Bob world.');
  return profiles[level - 1];
}

const zones = profiles.map((_, index) => {
  const course = getBobLevel(index + 1);
  return Object.freeze(
    [0.18, 0.43, 0.68].map((fraction, i) =>
      Object.freeze({
        id: `world-${i}`,
        x: i % 2 ? 1.6 : -1.6,
        z: course.length * fraction,
        width: 2.4,
        depth: 12,
      }),
    ),
  );
});

/** Immutable opportunity lanes, in metres, shared by physics and rendering.
 * Keep gates/finish clear so a mechanic never changes at the scoring boundary. */
export function bobWorldZones(level) {
  getBobWorld(level);
  return zones[level - 1];
}

/** Sample deterministic forces from the active simulation clock and position.
 * No wall time, random state, score or mutation. Bounded forces preserve input
 * authority; visual effects consume the exact same phase and lane geometry. */
export function bobWorldForces(state) {
  const w = getBobWorld(state.level),
    b = state.bob;
  const zone = bobWorldZones(state.level).find(
    (p) => Math.abs(b.x - p.x) < p.width / 2 && Math.abs(b.z - p.z) < p.depth / 2,
  );
  // Gold spring lanes preserve their authored reward arc across world rules.
  const corridor = getBobLevel(state.level).trampolines.some(
    (p) => Math.abs(b.x - p.x) < 1.7 && b.z >= p.z - 2 && b.z <= p.z + 18,
  );
  if (corridor || (state.effects?.springFlightUntilZ ?? -1) >= b.z)
    return { gravity: 11.5, response: 12, drag: 0, x: 0, z: 0, surface: 0, phase: 0, zone: null };
  const phase = Math.sin(((state.elapsed / 1000) * Math.PI) / 2);
  let gravity = w.gravity,
    response = w.response,
    drag = 0,
    x = 0,
    z = 0,
    lift = 0;
  if (w.kind === 'bubble') drag = 0.24;
  if (w.kind === 'tide') x = phase * 0.5;
  if (w.kind === 'aurora') x = phase * 0.3;
  if (zone) {
    if (['boost', 'express', 'belt'].includes(w.kind)) z = w.kind === 'express' ? 2 : 1;
    if (['lift', 'thermal', 'bubble', 'prism', 'bloom'].includes(w.kind) && b.y < 4)
      lift = w.kind === 'prism' ? 5 : 3;
    if (w.kind === 'vent' && phase > 0.25 && b.y < 4) lift = 5;
    if (w.kind === 'glide' && b.vy < 0) gravity *= 0.75;
    if (w.kind === 'grip') response = 24;
    if (w.kind === 'petal') x = phase * 0.7;
    if (w.kind === 'leaf') x = zone.x < 0 ? -0.65 : 0.65;
    if (w.kind === 'belt') x = zone.x < 0 ? 0.6 : -0.6;
    if (w.kind === 'vine') x = (zone.x - b.x) * 1.5;
    if (w.kind === 'bloom' && b.vy < 0) lift = 4;
    if (w.kind === 'celestial') gravity = 6;
  }
  const surface = w.kind === 'surf' ? 0.25 + 0.2 * Math.sin(b.z * 0.35 - state.elapsed * 0.002) : 0;
  if (w.kind === 'surf' && b.y < 1.3 + surface) z = 1.4;
  return {
    gravity: Math.max(4.5, gravity - lift),
    response,
    drag,
    x,
    z,
    surface,
    phase,
    zone: zone?.id || null,
  };
}
